import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { PopupLayout } from "../../../../PopupLayout/PopupLayout";

/**
 * OPEX Graph Popup Component
 * Displays a breakdown of operational expenditures as a pie chart
 */
const OpexGraphPopUp = (props, ref) => {
    // State and refs
    const [data, setData] = useState([]);
    const componentRef = useRef(null);
    const [update, setState] = useState(false);
    const forceUpdate = React.useCallback(() => setState(s => !s), []);

    // Expose forceUpdate method to parent components
    useImperativeHandle(ref, () => ({
        forceUpdate: () => {
            forceUpdate();
        }
    }));

    // Update graph when component opens or update is triggered
    useEffect(() => {
        if (props.isOpen) {
            setGraphSettings();
        }
    }, [props.isOpen, update]);

    /**
     * Get a unique name for equipment with unit number if available
     * @param {Object} equipment - The equipment object
     * @returns {String} - Formatted equipment name
     */
    const getUniqueEquipmentName = (equipment) => {
        // Get the unit number if available
        const unitNumber = equipment.prop('unitNumber');
        const unitPrefix = unitNumber ? `Unit ${unitNumber} ` : '';
        
        // Get the base name from the equipment
        let baseName = equipment.attributes.attrs.shapename.innerHTML;
        baseName = baseName.replace(/<p>|<\/p>/g, '');
        
        if (baseName.includes('<br>')) {
            baseName = equipment.attributes.unitType || 'Equipment';
        }
        
        // Return the formatted name with unit number prefix
        return `${unitPrefix}${baseName}`;
    };

    /**
     * Process OPEX data and set up the pie chart
     */
    const setGraphSettings = () => {
        const graph = props.getEconArrays[1];
        const econVars = props.getEconArrays[3];
        const allEquipment = graph.getElements();
        const newData = [];

        // 1. Extract all OPEX values from econVars
        const totalOpex = parseFloat(econVars.totalOpexDisplay || 0);
        const additionalOpex = parseFloat(econVars.additionalOpex || 0);
        const laborCosts = parseFloat(econVars.laborCosts || 0);
        const maintenanceCosts = parseFloat(econVars.maintenanceCosts || 0);
        const utilityCosts = parseFloat(econVars.utilityCosts || 0);
        const landRentandPropertyTax = parseFloat(econVars.landRentandPropertyTax || 0);
        const environmentalCosts = parseFloat(econVars.environmentalCosts || 0);
        const feedCostSumAnnual = parseFloat(econVars.feedCostSumAnnual || 0);
        
        console.log("Total OPEX:", totalOpex);

        // 2. First add the major cost categories that we always want to see
        // Labor costs
        if (laborCosts > 0.01) {
            newData.push({
                id: "Labor Costs",
                label: "Labor Costs",
                value: laborCosts
            });
        }

        // Maintenance costs
        if (maintenanceCosts > 0.01) {
            newData.push({
                id: "Maintenance Costs",
                label: "Maintenance Costs",
                value: maintenanceCosts
            });
        }

        // Utility costs
        if (utilityCosts > 0.01) {
            newData.push({
                id: "Utility Costs",
                label: "Utility Costs",
                value: utilityCosts
            });
        }

        // Property tax and land rent
        if (landRentandPropertyTax > 0.01) {
            newData.push({
                id: "Property Tax & Land Rent",
                label: "Property Tax & Land Rent",
                value: landRentandPropertyTax
            });
        }

        // Environmental charges
        if (environmentalCosts > 0.01) {
            newData.push({
                id: "Environmental Charges",
                label: "Environmental Charges",
                value: environmentalCosts
            });
        }

        if (feedCostSumAnnual > 0.01) {
            newData.push({
                id: "Feedstock Costs",
                label: "Total Feedstock Costs",
                value: feedCostSumAnnual
            });
        }

        // 3. Then process equipment-specific OPEX values
        let totalVesselOpex = 0;
        let equipmentItems = [];
        
        allEquipment.forEach((equipment) => {
            if (equipment.prop("isUnit") === "yes" && 
                equipment.attributes?.equipment_Properties?.unitType !== 'feed' && 
                equipment.attributes?.equipment_Properties?.unitType !== 'Feed' &&
                equipment.attributes?.equipment_Properties?.unitType !== 'product' &&
                equipment.attributes?.equipment_Properties?.unitType !== 'Product') {
                
                if (equipment.attributes?.equipment_Economics?.Opex?.Value) {
                    const opexValue = parseFloat(equipment.attributes.equipment_Economics.Opex.Value);
                    if (opexValue > 0.01) {
                        const equipName = getUniqueEquipmentName(equipment);
                        
                        equipmentItems.push({
                            id: equipName,
                            label: equipName,
                            value: opexValue
                        });
                        totalVesselOpex += opexValue;
                    }
                }
            }
        });
        
        // Sort equipment items by value (descending)
        equipmentItems.sort((a, b) => b.value - a.value);
        
        // If we have many items, group smaller ones
        const MAX_INDIVIDUAL_EQUIPMENT = 4; // Reduced from 6 to 4
        const SIGNIFICANCE_THRESHOLD = totalOpex * 0.01; // 1% of total OPEX

        // Add only significant equipment items individually
        let significantEquipment = equipmentItems.filter(item => item.value >= SIGNIFICANCE_THRESHOLD);
        
        // Further limit if there are still too many significant items
        if (significantEquipment.length > MAX_INDIVIDUAL_EQUIPMENT) {
            significantEquipment = significantEquipment.slice(0, MAX_INDIVIDUAL_EQUIPMENT);
        }
        
        // Add significant equipment to chart
        newData.push(...significantEquipment);
        
        // Group the remaining equipment into "Other Equipment"
        const otherEquipment = equipmentItems.filter(item => 
            !significantEquipment.some(sig => sig.id === item.id));
        
        if (otherEquipment.length > 0) {
            const otherSum = otherEquipment.reduce((sum, item) => sum + item.value, 0);
            if (otherSum > 0.01) {
                newData.push({
                    id: "Other Equipment",
                    label: "Other Equipment",
                    value: otherSum
                });
            }
        }
        
        console.log("Total Vessel OPEX:", totalVesselOpex);

        // 4. Calculate and add any remaining OPEX
        const accountedOpex = totalVesselOpex + laborCosts + maintenanceCosts + 
                             utilityCosts + landRentandPropertyTax + environmentalCosts + feedCostSumAnnual;
        const remainingOpex = totalOpex - accountedOpex - additionalOpex;
        
        console.log("Accounted OPEX:", accountedOpex);
        console.log("Remaining OPEX:", remainingOpex);

        // Add remaining OPEX if significant
        if (remainingOpex > 0.01) {
            newData.push({
                id: "Other Operating Expenses",
                label: "Other Operating Expenses",
                value: remainingOpex
            });
        }

        // 5. Add additional OPEX if it exists
        if (additionalOpex > 0.01) {
            newData.push({
                id: "Additional OPEX",
                label: "Additional OPEX",
                value: additionalOpex
            });
        }

        // 6. Fallback for when we have total OPEX but no breakdown
        if (newData.length === 0 && totalOpex > 0) {
            const baseOpex = totalOpex - additionalOpex;
            newData.push({
                id: "Base Operating Costs",
                label: "Base Operating Costs",
                value: baseOpex
            });
            
            if (additionalOpex > 0.01) {
                newData.push({
                    id: "Additional OPEX",
                    label: "Additional OPEX",
                    value: additionalOpex
                });
            }
        }
        
        // 7. Final sort by value to ensure consistent order
        newData.sort((a, b) => b.value - a.value);
        
        // 8. Assign colors that will be used for custom legend
        const colorScheme = ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', 
                             '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'];
        
        newData.forEach((item, index) => {
            item.color = colorScheme[index % colorScheme.length];
        });

        console.log("Final pie chart data:", newData);
        setData(newData);
    };

    // Render the component
    return (
        <>
            {props.isOpen && (
                                                <PopupLayout
                    componentRef={ref}
                    minWidth="750px"
                    title="Annual OPEX Breakdown"
                    closeFunction={props.onClose}
                    overflow={false}
                    pageX={window.innerWidth/2}
                    pageY={window.innerHeight/2}
                >
                    <div style={{width: '730px'}}>
                        <div style={{textAlign: 'center', marginBottom: '10px', fontWeight: 'bold', fontSize: '16px'}}>
                            Total OPEX: ${props.getEconArrays[3].totalOpexDisplay ? Math.round(parseFloat(props.getEconArrays[3].totalOpexDisplay)).toLocaleString('en-US') : '0'}
                        </div>
                        
                        {data.length > 0 ? (
                            <div style={{ height: '450px', position: 'relative' }}>
                                <ResponsivePie
                                    data={data}
                                    margin={{top: 40, right: 220, bottom: 150, left: 220}}
                                    innerRadius={0.4}
                                    padAngle={0.5}
                                    cornerRadius={3}
                                    activeOuterRadiusOffset={8}
                                    colors={{scheme: 'spectral'}}
                                    borderWidth={1}
                                    borderColor={{from: 'color', modifiers: [['darker', 0.2]]}}
                                    enableArcLabels={false}
                                    arcLinkLabelsSkipAngle={10}
                                    arcLinkLabelsStraightLength={16}
                                    arcLinkLabelsDiagonalLength={36}
                                    arcLinkLabelsTextOffset={6}
                                    arcLinkLabelsThickness={2}
                                    arcLinkLabelsColor={{ from: 'color' }}
                                    arcLinkLabel={d => {
                                        // Calculate percentage
                                        const total = data.reduce((sum, item) => sum + item.value, 0);
                                        const percentage = (d.value / total * 100).toFixed(1);
                                        
                                        // For very small slices, just return a simplified label
                                        if (percentage < 1) {
                                            return `${d.id}: ${percentage}%`;
                                        }
                                        
                                        return `${d.id}: $${Math.round(d.value).toLocaleString('en-US')} (${percentage}%)`;
                                    }}
                                    valueFormat={value =>
                                        `$${Math.round(value).toLocaleString('en-US')}`
                                    }
                                    legends={[]}
                                    tooltip={({ datum }) => (
                                        <div style={{
                                            background: 'white',
                                            padding: '9px 12px',
                                            border: '1px solid #ccc',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            borderRadius: '4px',
                                            maxWidth: '280px'
                                        }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                marginBottom: '5px'
                                            }}>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: datum.color,
                                                    marginRight: '8px'
                                                }} />
                                                <strong style={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word'
                                                }}>{datum.id}</strong>
                                            </div>
                                            <div style={{ marginLeft: '20px' }}>
                                                <span><strong>${Math.round(datum.value).toLocaleString('en-US')}</strong></span>
                                                <div style={{ fontSize: '0.9em', marginTop: '3px' }}>
                                                    {((datum.value / data.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}% of total
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <p>No OPEX data available</p>
                            </div>
                        )}
                        
                        {/* Custom legend */}
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            justifyContent: 'center',
                            marginTop: '5px',
                            padding: '0 10px',
                            maxHeight: '80px',
                            overflowY: 'auto'
                        }}>
                            {data.map((item, index) => (
                                <div key={index} style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    margin: '4px 8px',
                                    width: '140px'
                                }}>
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: item.color || 
                                            // Get colors from the spectral scheme
                                            ['#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', 
                                             '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'][index % 10],
                                        borderRadius: '50%',
                                        marginRight: '8px'
                                    }}></div>
                                    <span style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.id}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </PopupLayout>
            )}
        </>
    );
};

export default React.forwardRef((props, ref) => OpexGraphPopUp(props, ref));